/**
 * A learner's public page, from the outside: the address it lives at and the
 * client for `GET /api/profile/<tag>` (worker/profile.ts).
 *
 * This is the one screen in the product that a person who has never opened it
 * can be shown, so it is also the one that cannot lean on anything the app
 * already has. No socket, no session, no progress in `localStorage`, no
 * mission catalogue, and — the reason it is its own entry rather than a branch
 * inside App — no virtual machine. Everything it draws comes from one request
 * to one route, and that route answers a stranger.
 *
 * ## The address is a path, not a query
 *
 * Every other screen here is `?mission=`, `?debrief=`, `?profile=me`
 * (docs/browser.md, decision 5), and this one is `/u/<tag>` because of where
 * it ends up: pasted into a message, printed on a CV, read aloud. A path is
 * what a profile link looks like everywhere else on the web, and the asset
 * store already serves `index.html` for any path that is not a file
 * (`not_found_handling = "single-page-application"` in wrangler.toml), so it
 * costs no routing, no Worker invocation and no zone configuration to have.
 *
 * ## What comes back is checked, not trusted
 *
 * whois.ts's rule, for whois.ts's reason: the answer is JSON from a Worker
 * that may be a deploy ahead of or behind this bundle. Every field is
 * validated here and a field that fails takes only itself out — except the
 * name, which has nothing to fall back to, and whose absence means there is no
 * page.
 */
import { cleanSeed } from '@engineer-os/chat';
import { badgesEarned, CERTIFICATE_ID_PATTERN, certificateDefinition, type CertificateId, type EfficiencySummary, type VerificationBadge } from '@engineer-os/engine';
import { chatUrl } from './chat';
import { readSummary } from './efficiency';

/** One completion the server graded and accepted. */
export interface PublicCompletion {
  missionId: string;
  /** Unix milliseconds, the server's own clock. */
  verifiedAt: number;
  /** The mission's fixed award. Decided by the server; the browser never sends one. */
  xp: number;
}

/**
 * A certificate the learner holds, as a link to the page that checks it
 * (`/c/<id>`, lib/publicCertificate.ts). Only valid ones are sent: a
 * withdrawn certificate keeps its own page and loses its place here.
 */
export interface PublicCertificateLink {
  id: string;
  certificate: CertificateId;
  issuedAt: number;
}

/**
 * One learner, as anybody may see them.
 *
 * Deliberately small. There is no `cleared`, no `stars`, no rank and no last
 * seen — worker/profile.ts says why each of those is missing, and the short
 * version is that this page is readable by somebody with no reason to trust
 * whoever sent them the link, so everything on it has to be something the
 * server decided rather than something a browser reported.
 */
export interface PublicPerson {
  name: string;
  avatarSeed: string | null;
  joined: number;
  /** The grading contract these completions were accepted under. */
  version: string;
  completions: PublicCompletion[];
  /**
   * The worlds those completions add up to.
   *
   * The Worker sends these too, and this page ignores what it sends, for the
   * reason `xp` is summed here rather than read: a badge is a claim *about*
   * the rows below it, and a page that drew a badge it could not derive from
   * the rows a reader can see would be asking to be taken on faith at the one
   * moment it has no standing to.
   *
   * It also fails in the safe direction. A bundle older than the Worker draws
   * only the badges it has a table and a word for, which is the same rule the
   * level names follow; a badge it has never heard of is simply not there,
   * rather than being a shape with no sentence in it.
   */
  badges: VerificationBadge[];
  xp: number;
  /**
   * The certificates standing behind this learner. Empty from a Worker that
   * predates them, which is also what every account without one looks like.
   */
  certificates: PublicCertificateLink[];
  /**
   * What this learner's agents cost against par, as the median over every
   * metered checkpoint pass (docs/efficiency-plan.md, decision 4). Null below
   * `CREDENTIAL_MIN_PASSES`, and from a Worker that predates the meter.
   */
  efficiency: EfficiencySummary | null;
}

/** A tag as the wire spells it: 128 bits of SHA-256, lower case. */
const TAG = /^[0-9a-f]{32}$/;

/** The address of somebody's public page. The one place the shape is written down. */
export function personPath(tag: string): string {
  return `/u/${tag}`;
}

/**
 * Whose page this URL is, or null when it is not one.
 *
 * Read straight off the path by whoever needs the answer, the way `?lang` and
 * `?theme` are read (lib/locale.ts, lib/theme.ts), rather than routed through
 * App: the whole point of this screen is that App never mounts for it.
 *
 * A trailing slash is accepted because a link that has been through a chat
 * client, an email or a CV often has one, and refusing it would show a
 * stranger the level list instead of the page they were sent.
 */
export function personFromPath(pathname: string): string | null {
  const match = /^\/u\/([^/]+)\/?$/.exec(pathname);
  if (!match) return null;
  const tag = decodeURIComponent(match[1]!).toLowerCase();
  return TAG.test(tag) ? tag : null;
}

/**
 * The page's data, or null when there is nothing to show.
 *
 * Null covers every negative outcome — no such account, a deleted account, a
 * Worker that has never heard of this route, no network — because the screen
 * does the same thing for all of them: it says this page is not there. A
 * caller that could tell them apart would be a caller with four sentences to
 * choose between, and the true one among them is a fact about somebody who may
 * have asked to be gone.
 */
export async function fetchPerson(tag: string): Promise<PublicPerson | null> {
  if (!TAG.test(tag)) return null;
  try {
    const r = await fetch(chatUrl(`/api/profile/${tag}`).href, { cache: 'no-store' });
    if (!r.ok) return null;
    return readPerson(await r.json());
  } catch {
    return null;
  }
}

const stamp = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.trunc(v) : null);

/**
 * Exported for the tests, and only for them — whois.ts's arrangement, because
 * `chatUrl` reads `import.meta.env` and a plain Node test runner leaves that
 * undefined. Every decision in this file is in here.
 */
export function readPerson(body: unknown): PublicPerson | null {
  if (typeof body !== 'object' || body === null) return null;
  const b = body as { name?: unknown; avatarSeed?: unknown; joined?: unknown; version?: unknown; completions?: unknown; certificates?: unknown; efficiency?: unknown };

  if (typeof b.name !== 'string' || b.name.trim() === '') return null;
  const joined = stamp(b.joined);
  if (joined === null) return null;

  const completions: PublicCompletion[] = [];
  if (Array.isArray(b.completions)) {
    for (const raw of b.completions.slice(0, 64)) {
      if (typeof raw !== 'object' || raw === null) continue;
      const c = raw as { missionId?: unknown; verifiedAt?: unknown; xp?: unknown };
      const verifiedAt = stamp(c.verifiedAt);
      if (typeof c.missionId !== 'string' || c.missionId === '' || verifiedAt === null) continue;
      // A negative or absent award is zero rather than a dropped completion:
      // the badge is the fact that the server accepted the work, and the
      // number beside it is a detail of the award.
      const xp = typeof c.xp === 'number' && Number.isFinite(c.xp) && c.xp > 0 ? Math.trunc(c.xp) : 0;
      completions.push({ missionId: c.missionId, verifiedAt, xp });
    }
  }

  // A link this page cannot name or cannot open is not drawn: a certificate
  // this bundle has no title for, or an id that is not the shape `/c/` will
  // accept, would be a row that leads nowhere. One per kind, because an
  // account holds each certificate at most once and a second row would be the
  // Worker disagreeing with its own table.
  const certificates: PublicCertificateLink[] = [];
  if (Array.isArray(b.certificates)) {
    for (const raw of b.certificates.slice(0, 16)) {
      if (typeof raw !== 'object' || raw === null) continue;
      const c = raw as { id?: unknown; certificate?: unknown; issuedAt?: unknown };
      const issuedAt = stamp(c.issuedAt);
      if (typeof c.id !== 'string' || !CERTIFICATE_ID_PATTERN.test(c.id) || issuedAt === null) continue;
      if (typeof c.certificate !== 'string' || !certificateDefinition(c.certificate)) continue;
      if (certificates.some((held) => held.certificate === c.certificate)) continue;
      certificates.push({ id: c.id, certificate: c.certificate as CertificateId, issuedAt });
    }
  }

  return {
    name: b.name,
    avatarSeed: cleanSeed(b.avatarSeed),
    joined,
    version: typeof b.version === 'string' ? b.version : '',
    completions,
    badges: badgesEarned(completions.map((c) => c.missionId)),
    // Summed here rather than taken from the body. The server sends a total
    // and it is the same number, but a total that disagreed with the rows
    // under it would be a page arguing with itself, and the rows are what a
    // reader can check.
    xp: completions.reduce((sum, c) => sum + c.xp, 0),
    certificates,
    // Checked like every other field: a figure that does not read is not
    // drawn, and the rest of the page does not depend on it.
    efficiency: readSummary(b.efficiency),
  };
}
